/**
 * El estante de subastas en vivo de la portada (ADR 004 §2).
 *
 * El diagnóstico que lo justifica: la navegación de la tienda no tiene NINGÚN
 * punto de entrada a subastas, así que para llegar hay que saber ya que existen.
 * Este estante es esa puerta.
 *
 * Consume `cards.the-vault.mx/api/auctions?status=active` (público, sin auth,
 * con CORS) y pinta las que cierran primero. Es el mismo endpoint del panel del
 * mega menú — no hay una segunda API para lo mismo.
 *
 * Tres reglas que son diseño, no detalle:
 *
 * - **Vacío es ausente, no escaso.** Sin subastas vivas la sección no se
 *   renderiza. Un estante con un hueco donde deberían ir tres cartas se lee como
 *   que el sitio está muerto, y es peor que no tenerlo.
 * - **Falla en silencio para el comprador y en voz alta para quien depura.** Si
 *   la API truena, la portada no se entera. Pero se avisa por consola: el
 *   `catch` vacío es justo lo que dejó al widget del mega menú sin funcionar
 *   desde su despliegue sin que nadie lo notara (ADR 003).
 * - **El reloj es el del servidor.** `end_date` viene de la subasta; aquí solo
 *   se resta contra la hora local. Nunca un timer que arranca en la carga.
 *
 * Construye nodos DOM: nada de `innerHTML` con datos remotos.
 */
(function () {
  'use strict';

  var APP = 'https://cards.the-vault.mx';
  var ENDPOINT = APP + '/api/auctions?status=active';

  /** Eventos de producto (ADR 150). El helper vive en `header.liquid`. */
  function medir(evento, destino) {
    if (typeof window.vaultEvento !== 'function') return;
    window.vaultEvento(evento, destino === undefined ? null : String(destino));
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function money(value) {
    var amount = Number(value);
    if (!isFinite(amount)) return '';
    return '$ ' + amount.toLocaleString('es-MX');
  }

  /** "2d 4h" · "4h 12m" · "12m 30s" · "Cierra ya" */
  function restante(endDate) {
    var ms = new Date(endDate).getTime() - Date.now();
    if (!isFinite(ms) || ms <= 0) return 'Cierra ya';

    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);

    if (d > 0) return d + 'd ' + h + 'h';
    if (h > 0) return h + 'h ' + m + 'm';
    return m + 'm ' + (s % 60) + 's';
  }

  /**
   * `images` es posicional: primaria y respaldo, ya resueltas por la cadena del
   * ADR 087 cuando se creó la subasta. Se exige `https://` porque el arreglo es
   * dato remoto y termina en un `src`.
   */
  function urlsDeImagen(auction) {
    var lista = Array.isArray(auction.images) ? auction.images : [];
    return lista.filter(function (u) {
      return typeof u === 'string' && u.indexOf('https://') === 0;
    });
  }

  /**
   * Lo que la subasta le ahorra al comprador HOY, en pesos.
   *
   * `list_price` es lo más barato a lo que el catálogo vende esa misma
   * impresión (lo resuelve CardVault, misma cadena que el módulo de la ficha).
   * `null` cuando no hay ancla — nadie la surte— y entonces no hay descuento
   * que presumir: ni número ni prioridad.
   *
   * Se compara contra la puja ACTUAL, no contra la inicial: es lo que costaría
   * ganarla en este momento, y es lo único que el comprador puede verificar.
   */
  function descuento(auction) {
    var lista = Number(auction.list_price);
    var puja = Number(pujaActual(auction));
    if (!isFinite(lista) || !isFinite(puja) || lista <= 0) return null;
    var ahorro = lista - puja;
    if (ahorro <= 0) return null;
    return { pesos: ahorro, pct: Math.round((ahorro / lista) * 100) };
  }

  /** Sin pujas, la actual ES la inicial: es lo que hay que superar. */
  function pujaActual(auction) {
    return auction.current_price === null || auction.current_price === undefined
      ? auction.starting_price
      : auction.current_price;
  }

  function tarjeta(auction) {
    var link = el('a', 'vault-subastas-home__card');
    link.href = APP + '/auctions/' + encodeURIComponent(auction.id);
    link.addEventListener('click', function () {
      medir('homepage_module_card_click', auction.id);
    });

    var imagenes = urlsDeImagen(auction);
    var marco = el('div', 'vault-subastas-home__arte');
    if (imagenes.length > 0) {
      var img = document.createElement('img');
      img.className = 'vault-subastas-home__img';
      img.src = imagenes[0];
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      if (imagenes[1]) {
        img.addEventListener('error', function alRomperse() {
          img.removeEventListener('error', alRomperse);
          img.src = imagenes[1];
        });
      }
      marco.appendChild(img);
    }
    link.appendChild(marco);

    var cuerpo = el('div', 'vault-subastas-home__cuerpo');
    cuerpo.appendChild(el('span', 'vault-subastas-home__chip', 'En vivo'));
    cuerpo.appendChild(el('p', 'vault-subastas-home__titulo', auction.title || 'Subasta'));

    var datos = el('div', 'vault-subastas-home__datos');
    var puja = el('span', 'vault-subastas-home__puja');
    puja.appendChild(el('span', 'vault-subastas-home__etiqueta', 'Puja actual'));
    puja.appendChild(el('strong', 'vault-subastas-home__monto', money(pujaActual(auction))));
    datos.appendChild(puja);

    var reloj = el('span', 'vault-subastas-home__reloj', restante(auction.end_date));
    reloj.setAttribute('data-vault-subastas-home-end', auction.end_date);
    datos.appendChild(reloj);
    cuerpo.appendChild(datos);

    // El descuento contra el precio de tienda. Solo si existe: una subasta que
    // ya superó el precio de lista no tiene nada que presumir, y pintar un 0%
    // —o peor, un negativo— sería vender mal una subasta que va bien.
    var desc = descuento(auction);
    if (desc) {
      var linea = el('p', 'vault-subastas-home__descuento');
      linea.appendChild(el('strong', 'vault-subastas-home__pct', desc.pct + '% debajo'));
      linea.appendChild(el('span', 'vault-subastas-home__precio-tienda', ' de ' + money(auction.list_price) + ' en tienda'));
      cuerpo.appendChild(linea);
    }

    link.appendChild(cuerpo);
    return link;
  }

  /**
   * La impresión se reporta cuando el estante ENTRA EN PANTALLA, no cuando se
   * pinta. Es el denominador de la tasa de clic: contarlo al renderizar mediría
   * "cuánta gente cargó la portada", que ya sabemos, en vez de "a cuánta le
   * llegó el estante" — y el módulo va debajo del hero, así que la diferencia es
   * real.
   */
  function reportarImpresion(seccion) {
    var reportada = false;
    function reportar() {
      if (reportada) return;
      reportada = true;
      medir('homepage_module_impression', 'subastas');
    }

    if (typeof IntersectionObserver !== 'function') return reportar();

    var observador = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (entrada) {
        if (!entrada.isIntersecting) return;
        reportar();
        observador.disconnect();
      });
    }, { threshold: 0.3 });
    observador.observe(seccion);
  }

  /**
   * La sección aparece con un despliegue, no de golpe (ver el CSS de
   * `.vault-subastas-home:not([hidden])`). Se quita `hidden`, se mide cuánto va
   * a ocupar y se deja esa altura en `--vault-alto`; dos frames después entra
   * `is-visible`, que abre hasta ahí y escalona las tarjetas por su `--i`. Al
   * terminar, `is-settled` apaga todas las reglas de la animación y la sección
   * queda como si nunca se hubiera animado.
   */
  function desplegar(seccion) {
    var asentado = false;
    function asentar() {
      if (asentado) return;
      asentado = true;
      seccion.classList.add('is-settled');
      seccion.style.removeProperty('--vault-alto');
      seccion.style.removeProperty('--vault-pad');
    }

    // El padding abierto se lee ANTES de quitar `hidden`: con la sección aún
    // oculta el valor computado es el de .vault-modulo para este breakpoint; ya
    // visible, la regla del estado cerrado lo pone en cero.
    var pad = parseFloat(getComputedStyle(seccion).paddingTop) || 0;
    seccion.style.setProperty('--vault-pad', pad + 'px');
    seccion.hidden = false;
    // scrollHeight es solo el contenido (el padding está en cero). Se le suma
    // el padding abierto y un colchón de 4rem por lo que scrollHeight no cuenta
    // —márgenes del último hijo—; pasarse no se nota: max-height solo recorta.
    var rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    seccion.style.setProperty('--vault-alto', (seccion.scrollHeight + pad * 2 + rem * 4) + 'px');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        seccion.classList.add('is-visible');
      });
    });

    seccion.addEventListener('transitionend', function alAbrir(e) {
      if (e.target !== seccion || e.propertyName !== 'max-height') return;
      seccion.removeEventListener('transitionend', alAbrir);
      // Las tarjetas terminan de entrar después de que la altura ya abrió.
      setTimeout(asentar, 700);
    });
    // Con "reducir movimiento" no hay transición que termine: se asienta solo.
    setTimeout(asentar, 1800);
  }

  /**
   * El orden del estante.
   *
   * `descuento` (por defecto): manda el ahorro en PESOS, no el porcentaje. Un
   * 40% sobre una carta de $80 son $32; un 15% sobre una de $4,000 son $600, y
   * es la segunda la que hace que alguien deje lo que está haciendo. El
   * porcentaje se sigue PINTANDO —es lo legible de un vistazo— pero no es lo
   * que decide quién va primero.
   *
   * Las que no tienen ancla de precio van al final: no es que descuenten poco,
   * es que no sabemos, y presumirlas arriba sería inventar.
   *
   * `cierre`: las que cierran primero, que es el orden por urgencia.
   *
   * En los dos casos el desempate es la fecha de cierre.
   */
  function ordenar(subastas, criterio) {
    if (criterio === 'cierre') {
      subastas.sort(function (a, b) { return new Date(a.end_date) - new Date(b.end_date); });
      return subastas;
    }

    subastas.sort(function (a, b) {
      var da = descuento(a);
      var db = descuento(b);
      var va = da ? da.pesos : -1;
      var vb = db ? db.pesos : -1;
      if (va !== vb) return vb - va;
      return new Date(a.end_date) - new Date(b.end_date);
    });
    return subastas;
  }

  function pintar(seccion, lista, subastas) {
    subastas.forEach(function (auction, i) {
      var card = tarjeta(auction);
      card.style.setProperty('--i', String(i));
      lista.appendChild(card);
    });

    desplegar(seccion);
    reportarImpresion(seccion);

    setInterval(function () {
      var relojes = seccion.querySelectorAll('[data-vault-subastas-home-end]');
      Array.prototype.forEach.call(relojes, function (reloj) {
        reloj.textContent = restante(reloj.getAttribute('data-vault-subastas-home-end'));
      });
    }, 1000);
  }

  function init() {
    var seccion = document.querySelector('[data-vault-subastas-home]');
    if (!seccion) return;

    var lista = seccion.querySelector('[data-vault-subastas-home-lista]');
    if (!lista) return;

    var cuantas = parseInt(seccion.getAttribute('data-cuantas'), 10) || 3;

    fetch(ENDPOINT)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var subastas = Array.isArray(data) ? data : (data && data.auctions) || [];
        ordenar(subastas, seccion.getAttribute('data-prioridad'));
        // Sin subastas vivas la sección se queda oculta: es la regla de US-X6.
        if (subastas.length === 0) return;
        pintar(seccion, lista, subastas.slice(0, cuantas));
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[vault-subastas-home] falló ' + ENDPOINT + ' — ' + err.message);
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
